import {
  BriefAdvancedSearchData,
  useDeleteAdvancedSearch,
  useGetAdvancedSearchByResource,
} from "@/api/advanced-search";
import { ColumnDef, Row } from "@tanstack/react-table";
import { Trash2, TriangleAlert } from "lucide-react";
import { DataTable } from "../ui/datatable";
import { useAdvancedSearch } from "@/context/advanced-search/AdvancedSearchContext";
import { useCallback, useMemo } from "react";
import { toast } from "@/hooks/use-toast";
import { useTranslation } from "react-i18next";
import { TFunction } from "i18next";
import {
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from "../ui/tooltip";
import { tWithKey } from "@/lib/i18n-fallback";
import {
  isSearchFieldHidden,
  readTableVisibility,
  resourceToTable,
} from "@/config/field-visibility/search-field-visibility";
import {
  TableRowActionsMenu,
  type TableRowAction,
} from "@/components/table/TableRowActionsMenu";
import { ACTION_I18N_KEYS } from "@/components/table/action-i18n-keys";
import { createActionsColumnDef } from "@/components/table/table-actions-column";

const baseColumns = (
  t: TFunction,
  getMaskedFieldKeys: (row: Row<BriefAdvancedSearchData>) => string[],
): ColumnDef<BriefAdvancedSearchData>[] => [
  {
    id: "name",
    size: 1000,
    header: t("savedAdvancedSearchName"),
    cell: ({ row }) => {
      const maskedKeys = getMaskedFieldKeys(row);
      return (
        <div className="flex items-center gap-2">
          <span>{row.original.name}</span>
          {maskedKeys.length > 0 && (
            <TooltipProvider>
              <Tooltip>
                <TooltipTrigger asChild>
                  <span className="inline-flex items-center text-amber-600">
                    <TriangleAlert className="h-4 w-4" />
                  </span>
                </TooltipTrigger>
                <TooltipContent className="max-w-xs">
                  {tWithKey(
                    t,
                    "advancedSearch.maskedFieldWarning",
                    "Cette recherche risque de ne pas fonctionner : un ou plusieurs champs sont masqués",
                  )}
                  {" : "}
                  {maskedKeys.map((k) => t(k)).join(", ")}
                </TooltipContent>
              </Tooltip>
            </TooltipProvider>
          )}
        </div>
      );
    },
  },
  {
    id: "createdAt",
    size: 1,
    header: t("createdAt"),
    cell: ({ row }) => (
      <>
        <span>
          {new Date(
            row.original.createdAt as unknown as string,
          ).toLocaleDateString()}
        </span>
      </>
    ),
  },
];

export const SavedAdvancedSearchQueriesList = ({
  resource,
}: {
  resource: string;
}) => {
  const { data } = useGetAdvancedSearchByResource(resource);

  const { t } = useTranslation();

  const advancedSearch = useAdvancedSearch();

  const deleteMutation = useDeleteAdvancedSearch();

  const table = resourceToTable(resource);
  const visibility = useMemo(
    () => (table ? readTableVisibility(table) : {}),
    [table],
  );

  // Field keys of a saved query that are currently masked in the fiche.
  const getMaskedFieldKeys = useCallback(
    (row: Row<BriefAdvancedSearchData>): string[] =>
      (row.original.query?.fields ?? [])
        .map((field) => field.key)
        .filter(
          (key): key is string =>
            !!key && isSearchFieldHidden(table, key, visibility),
        ),
    [table, visibility],
  );

  function onRowClick(row: Row<BriefAdvancedSearchData>) {
    const element = row.original;

    advancedSearch.setAdvancedSearchQuery({
      fields: element.query.fields,
      searchMode: element.query.searchMode,
      name: element.name,
    });
  }

  const onDelete = useCallback(
    (row: Row<BriefAdvancedSearchData>) => {
      deleteMutation.mutate(row.original.id, {
        onError: (e) => {
          console.error("error when deleting the AdvancedSearch :", e);
          toast({
            title: "Error",
            description: "deleteAdvancedSearchErrorMessage",
            duration: 1000,
            variant: "error",
          });
        },
      });
    },
    [deleteMutation],
  );

  const columns = useMemo(
    () => [
      ...baseColumns(t, getMaskedFieldKeys),
      createActionsColumnDef<BriefAdvancedSearchData>(({ row }) => {
        const actions: TableRowAction[] = [
          {
            id: "delete",
            label: t(ACTION_I18N_KEYS.delete),
            icon: Trash2,
            destructive: true,
            onSelect: () => onDelete(row),
          },
        ];

        return (
          <div
            className="flex justify-end"
            onClick={(event) => event.stopPropagation()}
          >
            <TableRowActionsMenu actions={actions} />
          </div>
        );
      }),
    ],
    [onDelete, t, getMaskedFieldKeys],
  );

  return (
    <div className="flex flex-col gap-6">
      <h2 className="font-semibold">{t("storedSearch")}</h2>
      <DataTable columns={columns} data={data ?? []} onRowClick={onRowClick} />
    </div>
  );
};
